import {
  Home,
  User,
  Brain,
  Dumbbell,
  Activity,
  MessageCircle,
  UserPlus,
  TrendingUp,
  ChefHat,
  FileText,
  Users,
  Link2,
  Clock,
  Archive,
  LayoutDashboard,
  FolderOpen,
  ListOrdered,
  UtensilsCrossed,
  Utensils,
  LineChart,
  ScanSearch,
  Pill,
  Zap,
  Settings,
  DollarSign,
  ClipboardCheck,
  Inbox,
  CalendarDays,
  Video,
  type LucideIcon,
} from 'lucide-react';

import { SHOW_COACH_NUTRITION_KPI_DASHBOARD, SHOW_COACH_PROGRAMS_KPI_DASHBOARD } from './coachUiVisibility';

export interface NavSubItem {
  id: string;
  label: string;
  parentId: string;
  icon?: LucideIcon;
  /** When true, this sub-item is omitted from the sidebar (see `SidebarNavItem`). */
  hidden?: boolean;
}

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  subItems?: NavSubItem[];
}

export const getCoachNavigation = (unreadCount: number = 0): NavItem[] => [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
  },
  {
    id: 'clients',
    label: 'Clients',
    icon: User,
    subItems: [
      { id: 'clients-all', label: 'All Clients', parentId: 'clients', icon: Users },
      { id: 'clients-connected', label: 'Connected', parentId: 'clients', icon: Link2 },
      { id: 'clients-pending', label: 'Pending', parentId: 'clients', icon: Clock },
      { id: 'clients-archived', label: 'Archived', parentId: 'clients', icon: Archive },
    ],
  },
  {
    id: 'chat',
    label: 'Chat',
    icon: MessageCircle,
    badge: unreadCount,
  },
  {
    id: 'calendar',
    label: 'Calendar',
    icon: CalendarDays,
  },
  {
    id: 'add-client',
    label: 'Add Client',
    icon: UserPlus,
  },
  {
    id: 'programs',
    label: 'Programs',
    icon: Dumbbell,
    subItems: [
      {
        id: 'programs-dashboard',
        label: 'Dashboard',
        parentId: 'programs',
        icon: LayoutDashboard,
        hidden: !SHOW_COACH_PROGRAMS_KPI_DASHBOARD,
      },
      { id: 'programs-all', label: 'All Programs', parentId: 'programs', icon: FolderOpen },
      { id: 'programs-workouts', label: 'Workouts', parentId: 'programs', icon: Dumbbell },
      { id: 'programs-exercises', label: 'Exercises', parentId: 'programs', icon: ListOrdered },
    ],
  },
  {
    id: 'nutrition',
    label: 'Nutrition',
    icon: ChefHat,
    subItems: [
      {
        id: 'nutrition-dashboard',
        label: 'Dashboard',
        parentId: 'nutrition',
        icon: LayoutDashboard,
        hidden: !SHOW_COACH_NUTRITION_KPI_DASHBOARD,
      },
      { id: 'nutrition-meal-plans', label: 'Meal Plans', parentId: 'nutrition', icon: UtensilsCrossed },
      { id: 'nutrition-clients', label: 'Clients', parentId: 'nutrition', icon: Users },
      { id: 'nutrition-meals', label: 'Meals', parentId: 'nutrition', icon: Utensils },
    ],
  },
  {
    id: 'supplements',
    label: 'Supplements',
    icon: Pill,
  },
  {
    id: 'forms-trackers',
    label: 'Check-Ins',
    icon: ClipboardCheck,
    subItems: [
      { id: 'forms-trackers-inbox', label: 'Inbox', parentId: 'forms-trackers', icon: Inbox },
      { id: 'forms-trackers-forms', label: 'Forms', parentId: 'forms-trackers', icon: FileText },
      { id: 'forms-trackers-trackers', label: 'Trackers', parentId: 'forms-trackers', icon: LineChart },
    ],
  },
  {
    id: 'ai',
    label: 'AI Tools',
    icon: Brain,
    subItems: [
      { id: 'ai-patterns', label: 'Pattern Detection', parentId: 'ai', icon: ScanSearch },
      { id: 'ai-chat', label: 'Chat Analysis', parentId: 'ai', icon: MessageCircle },
    ],
  },
  {
    id: 'automations',
    label: 'Automations',
    icon: Zap,
  },
  {
    id: 'client-billing',
    label: 'Client Billing',
    icon: DollarSign,
  },
  // The upgrade hub was previously reachable only by hitting a paywall or by
  // typing /upgrade, so a coach who decided to pay had nowhere to click.
  {
    id: 'upgrade',
    label: 'Upgrade',
    icon: TrendingUp,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
  },
];

export const getClientNavigation = (
  unreadCount: number = 0,
  checkinReplies: number = 0,
  supplementChanges: number = 0,
  formAssignments: number = 0,
  paymentPendingCount: number = 0,
  showPayments: boolean = false,
): NavItem[] => [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Home,
  },
  {
    id: 'workouts',
    label: 'Workouts',
    icon: Dumbbell,
  },
  {
    id: 'nutrition',
    label: 'Nutrition',
    icon: ChefHat,
  },
  {
    id: 'supplements',
    label: 'Supplements',
    icon: Pill,
    badge: supplementChanges,
  },
  {
    id: 'goals',
    label: 'Check-Ins',
    icon: ClipboardCheck,
    badge: checkinReplies + formAssignments,
  },
  {
    id: 'videos',
    label: 'Videos',
    icon: Video,
  },
  {
    id: 'progress',
    label: 'Progress',
    icon: TrendingUp,
  },
  {
    id: 'messages',
    label: 'Messages',
    icon: MessageCircle,
    badge: unreadCount,
  },
  ...(showPayments
    ? [
        {
          id: 'payments',
          label: 'Payments',
          icon: DollarSign,
          badge: paymentPendingCount,
        },
      ]
    : []),
  {
    id: 'wearables',
    label: 'Health',
    icon: Activity,
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
  },
];

// Helper to get parent ID from a submenu item ID
export const getParentId = (itemId: string): string => {
  if (itemId.includes('-')) {
    return itemId.split('-')[0];
  }
  return itemId;
};

// Helper to check if an item is a submenu item
export const isSubMenuItem = (itemId: string, navItems: NavItem[]): boolean => {
  return navItems.some((item) => item.subItems?.some((subItem) => subItem.id === itemId));
};
